import { useState } from "react";
import {
  Button,
  ButtonVariant,
  Text,
  TextField,
  Toggle,
  Typeahead,
} from "@merge-api/merge-javascript-shared";
import { Check } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { Playbook } from "../types";
import { ConductorFlag } from "../flags/types";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import { conductorPost } from "../conductorApi";
import { useConductorResource } from "../hooks/useConductorResource";
import ConductorDrawer from "./ConductorDrawer";
import { FieldLabel, Section, Select } from "./ConductorFormFields";

interface Props {
  integrationId: string;
  /** Available categories for the integration. When >1, the user must pick one. */
  categories: string[];
  onClose: () => void;
  onCreated: (playbookId: string | null) => void;
}

// Creates an (entry-less) playbook for the integration. Entries are attached
// afterward from the playbook detail view. Scope (org / linked account) is
// optional; blank means the playbook matches all at that level.
const PlaybookEditorDrawer = ({ integrationId, categories, onClose, onCreated }: Props) => {
  const resolvedCategory = categories.length === 1 ? categories[0] : undefined;
  const needsCategoryChoice = !resolvedCategory && categories.length > 1;

  const [selectedCategory, setSelectedCategory] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [linkedAccountId, setLinkedAccountId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [rolloutFlagName, setRolloutFlagName] = useState("");
  const [onlyRunBlueprintsOnPlaybook, setOnlyRunBlueprintsOnPlaybook] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Supplying an id makes this a true create. Without one, Conductor's legacy
  // upsert resolves a playbook by scope and would overwrite an existing one.
  const [newPlaybookId] = useState(() => uuidv4());
  const { data: flags, isLoading: flagsLoading } = useConductorResource<ConductorFlag[]>(
    "/conductor/flags"
  );
  const flagNames = (flags || []).map((flag) => flag.name);
  // Keep an already-set flag selectable even if it's since been deleted/renamed.
  const flagOptions =
    rolloutFlagName && !flagNames.includes(rolloutFlagName)
      ? [rolloutFlagName, ...flagNames]
      : flagNames;

  const orgId = organizationId.trim() || null;
  const linkedId = linkedAccountId.trim() || null;
  const effectiveCategory = resolvedCategory ?? selectedCategory;
  const canSave = !!effectiveCategory && !isSaving;

  const save = async () => {
    setIsSaving(true);
    const { ok, data, error } = await conductorPost<Playbook>("/conductor/playbooks", {
      id: newPlaybookId,
      integration_id: integrationId,
      organization_id: orgId,
      linked_account_id: linkedId,
      category: effectiveCategory,
      enabled,
      rollout_flag_name: rolloutFlagName.trim() || null,
      only_run_blueprints_on_playbook: onlyRunBlueprintsOnPlaybook,
      entries: [],
    });
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Playbook created");
      onCreated(data?.id ?? newPlaybookId);
    } else {
      showErrorToast(error || "Failed to create playbook");
    }
  };

  return (
    <ConductorDrawer
      title="New playbook"
      onClose={onClose}
      footer={
        <>
          <span />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={ButtonVariant.SecondaryGray}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant={ButtonVariant.PrimaryGray}
              leftIcon={<Check size={14} />}
              onClick={save}
              disabled={!canSave}
            >
              Create playbook
            </Button>
          </div>
        </>
      }
    >
      <Section title="Scope">
        <Text variant="sm" className="mb-4 block text-gray-50">
          A playbook is resolved for a linked account by integration, then organization, then linked
          account. Leave a field blank to match all at that level.
        </Text>
        <div className="mb-4">
          <FieldLabel>Category</FieldLabel>
          {needsCategoryChoice ? (
            <Select value={selectedCategory} onChange={setSelectedCategory}>
              <option value="">Select a category…</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Select>
          ) : (
            <Text variant="md" className="font-mono text-gray-80">
              {effectiveCategory || "-"}
            </Text>
          )}
        </div>
        <div className="mb-4">
          <FieldLabel>Organization ID</FieldLabel>
          <TextField
            className="bg-white"
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            placeholder="blank = all organizations"
          />
        </div>
        <div className="mb-4">
          <FieldLabel>Linked account ID</FieldLabel>
          <TextField
            className="bg-white"
            value={linkedAccountId}
            onChange={(e) => setLinkedAccountId(e.target.value)}
            placeholder="blank = all linked accounts"
          />
        </div>
        <div className="flex items-center justify-between">
          <FieldLabel>Enabled</FieldLabel>
          <Toggle checked={enabled} onChange={setEnabled} />
        </div>
      </Section>

      <Section title="Settings">
        <div className="mb-4">
          <FieldLabel>Rollout flag name</FieldLabel>
          <Typeahead
            options={flagOptions}
            value={rolloutFlagName || null}
            loading={flagsLoading}
            onChange={(_: unknown, value: string | null) => setRolloutFlagName(value ?? "")}
            placeholder="blank = ungated"
          />
          <Text variant="sm" className="mt-1 block text-gray-50">
            Gates this playbook behind a conductor flag, so it can coexist with an ungated playbook
            at the same scope while it rolls out.
          </Text>
        </div>
        <div className="flex items-center justify-between">
          <FieldLabel>Only run blueprints on playbook</FieldLabel>
          <Toggle checked={onlyRunBlueprintsOnPlaybook} onChange={setOnlyRunBlueprintsOnPlaybook} />
        </div>
      </Section>
    </ConductorDrawer>
  );
};

export default PlaybookEditorDrawer;
